import React, { useCallback, useMemo, useState } from 'react';
import { CommonCss } from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import { hiCol } from 'components/grid/hiGrid';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { loadDepts, filterDepts } from 'master/deptStore';
import { loadOrSeedProjects } from 'budget/budgetProjectStore';
import { loadSubjectsByFilter } from 'budget/budgetStructureStore';
import {
  buildSubjectDispCode,
  buildSubjectDispName,
} from 'budget/budgetSubjectHistoryStore';

function formatCodeName(code, name) {
  const c = String(code || '').trim();
  const n = String(name || '').trim();
  if (c && n) return `${c} · ${n}`;
  return c || n || '';
}

function formatMultiCodeName(items) {
  const list = (items || [])
    .map((it) => formatCodeName(it.code, it.name))
    .filter(Boolean);
  if (!list.length) return '';
  if (list.length <= 2) return list.join(', ');
  return `${list.slice(0, 2).join(', ')} 외 ${list.length - 2}건`;
}

function joinCodes(values) {
  return (values || [])
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join(',');
}

export function emptyBudgetSearchCodes() {
  return {
    deptCode: '',
    deptName: '',
    deptDisp: '',
    projectCode: '',
    projectName: '',
    projectDisp: '',
    subjectCode: '',
    subjectName: '',
    subjectDisp: '',
  };
}

/**
 * 조회 FormArea용 부서·사업·과목 코드조회 (CodeLookupDialog 다중선택)
 */
export function useBudgetSearchCodeLookup({
  getDims,
  setValue,
  prefsUserId = '',
  inoutDiv = '지출',
  sizeKeyPrefix = 'codeLookup-inquiry',
} = {}) {
  const [lookup, setLookup] = useState({ open: false, type: null, rows: [] });

  const closeLookup = useCallback(
    () => setLookup({ open: false, type: null, rows: [] }),
    []
  );

  const buildLookupRows = useCallback(
    async (type) => {
      const dims = { ...(getDims?.() || {}), inoutDiv };
      const tenant = dims.tenantCode;
      if (type === 'dept') {
        const list = filterDepts(await loadDepts(tenant), { useYn: 'Y' });
        return list.map((r) => ({
          ...r,
          id: r.deptUid || r.deptCode,
          code: r.deptCode,
          name: r.deptName,
        }));
      }
      if (type === 'project') {
        const loaded = (await loadOrSeedProjects(dims)) || [];
        return loaded
          .filter((r) => r.useYn !== 'N')
          .map((r) => ({
            ...r,
            mod: '',
            id: r.projectUid || r.projectCode,
            code: r.projectCode,
            name: r.projectName,
          }));
      }
      if (type === 'subject') {
        const all = (await loadSubjectsByFilter({ ...dims, inoutDiv })) || [];
        const finals = all.filter((r) => r.finalYn === 'Y' && r.useYn !== 'N' && r.mod !== 'D');
        return finals.map((r) => {
          const disp = buildSubjectDispCode(r, all);
          const dispName = buildSubjectDispName(r, all);
          return {
            ...r,
            id: r.subjectUid || `${r.budgetType}-${r.subjectCode}`,
            code: disp,
            name: dispName,
            subjectDispCode: disp,
            subjectDispName: dispName,
            subjectName: dispName,
            leafSubjectName: r.subjectName,
          };
        });
      }
      return [];
    },
    [getDims, inoutDiv]
  );

  const openSearchLookup = useCallback(
    async (type) => {
      const rows = await buildLookupRows(type);
      setLookup({ open: true, type, rows });
    },
    [buildLookupRows]
  );

  const clearSearchCodeFields = useCallback(
    (kind) => {
      if (kind === 'dept') {
        setValue('deptCode', '');
        setValue('deptName', '');
        setValue('deptDisp', '');
        return;
      }
      if (kind === 'project') {
        setValue('projectCode', '');
        setValue('projectName', '');
        setValue('projectDisp', '');
        return;
      }
      if (kind === 'subject') {
        setValue('subjectCode', '');
        setValue('subjectName', '');
        setValue('subjectDisp', '');
      }
    },
    [setValue]
  );

  const lookupConfig = useMemo(() => {
    const rows = Array.isArray(lookup.rows) ? lookup.rows : [];
    if (lookup.type === 'dept') {
      return {
        title: '부서 조회 (검색)',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 110 }),
          hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 160 }),
          hiCol.roTextColumn({ header: '경로', field: 'pathCode', size: 200 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordLabel: '검색',
          hintCountLabel: '부서',
          keywordFields: ['deptCode', 'deptName', 'code', 'name', 'pathCode'],
        },
      };
    }
    if (lookup.type === 'project') {
      return {
        title: '사업 조회 (검색)',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 110 }),
          hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 180 }),
          hiCol.roTextColumn({ header: '레벨', field: 'levelName', size: 90 }),
          hiCol.roTextColumn({ header: '경로', field: 'pathCode', size: 180 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordLabel: '검색',
          hintCountLabel: '사업',
          keywordFields: ['projectCode', 'projectName', 'code', 'name', 'pathCode'],
        },
      };
    }
    if (lookup.type === 'subject') {
      return {
        title: '예산과목 조회 (검색)',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '결합코드', field: 'subjectDispCode', size: 140 }),
          hiCol.roTextColumn({ header: '과목명', field: 'subjectDispName', size: 260 }),
          hiCol.roTextColumn({ header: '레벨', field: 'levelName', size: 80 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordLabel: '검색',
          hintCountLabel: '최종과목',
          keywordFields: [
            'subjectDispCode',
            'subjectCode',
            'code',
            'subjectDispName',
            'subjectName',
            'leafSubjectName',
            'name',
          ],
        },
      };
    }
    return { title: '코드 조회', rows: [], columns: [], enableSearch: false, searchConfig: {} };
  }, [lookup.type, lookup.rows]);

  const applyLookup = useCallback(
    (payload) => {
      if (!payload) return;
      const rows = (Array.isArray(payload) ? payload : [payload]).filter(Boolean);
      if (!rows.length) {
        clearSearchCodeFields(lookup.type);
        closeLookup();
        return;
      }
      if (lookup.type === 'dept') {
        const items = rows
          .map((r) => ({
            code: String(r.deptCode || '').trim(),
            name: String(r.deptName || '').trim(),
          }))
          .filter((x) => x.code);
        setValue('deptCode', joinCodes(items.map((x) => x.code)));
        setValue('deptName', items.map((x) => x.name).filter(Boolean).join(', '));
        setValue('deptDisp', formatMultiCodeName(items));
      } else if (lookup.type === 'project') {
        const items = rows
          .map((r) => ({
            code: String(r.projectCode || '').trim(),
            name: String(r.projectName || '').trim(),
          }))
          .filter((x) => x.code);
        setValue('projectCode', joinCodes(items.map((x) => x.code)));
        setValue('projectName', items.map((x) => x.name).filter(Boolean).join(', '));
        setValue('projectDisp', formatMultiCodeName(items));
      } else if (lookup.type === 'subject') {
        const items = rows
          .map((r) => {
            const code = String(r.subjectDispCode || r.subjectCode || '').trim();
            const name = String(r.subjectDispName || r.subjectName || '').trim();
            const leaf = String(r.subjectCode || code).trim();
            return { code, name, leaf };
          })
          .filter((x) => x.code);
        setValue('subjectCode', joinCodes(items.flatMap((x) => [x.leaf, x.code].filter(Boolean))));
        setValue('subjectName', items.map((x) => x.name).filter(Boolean).join(', '));
        setValue('subjectDisp', formatMultiCodeName(items));
      }
      closeLookup();
    },
    [lookup.type, setValue, clearSearchCodeFields, closeLookup]
  );

  const searchCodeFields = useMemo(
    () => [
      {
        name: 'deptDisp',
        label: '부서',
        type: 'text',
        width: CommonCss.w180,
        labelWidth: SEARCH_LABEL_W,
        readonly: true,
        showSearch: true,
        searchTitle: '부서 조회',
        onSearch: () => openSearchLookup('dept'),
        title: '멀티체크 선택 · [조건 해제]로 전체 조회',
        placeholder: '전체',
      },
      {
        name: 'projectDisp',
        label: '사업',
        type: 'text',
        width: CommonCss.w200,
        labelWidth: SEARCH_LABEL_W,
        readonly: true,
        showSearch: true,
        searchTitle: '사업 조회',
        onSearch: () => openSearchLookup('project'),
        title: '멀티체크 선택 · [조건 해제]로 전체 조회',
        placeholder: '전체',
      },
      {
        name: 'subjectDisp',
        label: '과목',
        type: 'text',
        width: CommonCss.w220,
        labelWidth: SEARCH_LABEL_W,
        readonly: true,
        showSearch: true,
        searchTitle: '예산과목 조회',
        onSearch: () => openSearchLookup('subject'),
        title: '멀티체크 선택 · [조건 해제]로 전체 조회',
        placeholder: '전체',
      },
    ],
    [openSearchLookup]
  );

  const lookupDialog = (
    <CodeLookupDialog
      open={lookup.open}
      title={lookupConfig.title}
      columns={lookupConfig.columns}
      rows={lookupConfig.rows}
      enableSearch={!!lookupConfig.enableSearch}
      searchConfig={lookupConfig.searchConfig}
      multiSelect
      prefsUserId={prefsUserId}
      sizeKey={lookup.type ? `${sizeKeyPrefix}-${lookup.type}` : sizeKeyPrefix}
      onSelect={applyLookup}
      onClose={closeLookup}
    />
  );

  return { lookupDialog, openSearchLookup, searchCodeFields };
}
